import React from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import stylizr from '@aftonbladet/stylizr';

import type {
  ExtendedLinkMarkup,
  ExtendedMarkup,
  ListBlock,
  RichTextElement as RichTextElementType,
  SymbolMarkup,
  Text,
  TextBlock,
} from '../../../core/data-sources/content-components/types.js';

import {
  ListBlockTypes,
  MarkupOptions,
  TextBlockTypes,
} from '../../../core/data-sources/content-components/types.js';
import { WordExplainer } from '../components/articles/articleComponents/WordExplainer.js';

const stylizrInstance = stylizr({
  [MarkupOptions.Bold]: (text, key) => <strong key={key}>{text}</strong>,
  [MarkupOptions.Italic]: (text, key) => <em key={key}>{text}</em>,
  [MarkupOptions.SuperScript]: (text, key) => <sup key={key}>{text}</sup>,
  [MarkupOptions.SubScript]: (text, key) => <sub key={key}>{text}</sub>,
  [MarkupOptions.Highlight]: (text, key) => <mark key={key}>{text}</mark>,
  [MarkupOptions.Link]: (text, key, data: ExtendedLinkMarkup) => {
    const additionalAttributes = 'attributes' in data ? data.attributes : {};

    return (
      <a
        key={key}
        className="link"
        href={data.uri}
        {...additionalAttributes}
        {...(data.onClick ? { onClick: data.onClick } : {})}
      >
        {text}
      </a>
    );
  },
  [MarkupOptions.LinkInternal]: (text, key, data: ExtendedLinkMarkup) =>
    data.uri ? (
      <a
        key={key}
        className="link"
        href={data.uri.split(':').pop()}
        {...(data.onClick ? { onClick: data.onClick } : {})}
      >
        {text}
      </a>
    ) : (
      ''
    ),
  [MarkupOptions.Explanation]: (
    text,
    key,
    data: SymbolMarkup & { termText?: string },
  ) =>
    data.symbol ? (
      <WordExplainer
        key={key}
        term={data.termText ?? text}
        explanation={data.symbol}
      />
    ) : (
      ''
    ),
} as Record<
  string,
  (text: string, key: string, data: ExtendedMarkup) => unknown
>);

interface RichTextElementProps {
  content: Text;
}

export const RichTextElement: React.FC<RichTextElementProps> = ({
  content: richText,
}) => {
  const markup = richText.markup?.map((m) =>
    m.type === MarkupOptions.Explanation
      ? { ...m, termText: richText.value.slice(m.offset, m.offset + m.length) }
      : m,
  );

  return stylizrInstance(richText.value, markup);
};

interface RichTextListBlockProps {
  content: ListBlock;
}

export const RichTextListBlock: React.FC<RichTextListBlockProps> = ({
  content: listBlock,
}) => {
  const listItems = listBlock.items.map((text, i) => {
    return (
      <li key={i}>
        <RichTextElement content={text} />
      </li>
    );
  });

  switch (listBlock.blockType) {
    case ListBlockTypes.Unordered:
    case ListBlockTypes.Checked:
      return <ul>{listItems}</ul>;
    case ListBlockTypes.Ordered:
      return <ol>{listItems}</ol>;
  }
};

interface RichTextBlockProps {
  content: RichTextElementType;
}

export const RichTextBlock: React.FC<RichTextBlockProps> = ({ content }) => {
  if ((content as TextBlock).text) {
    const textBlock = content as TextBlock;

    switch (textBlock.blockType) {
      case TextBlockTypes.Heading:
        return (
          <h1>
            <RichTextElement content={textBlock.text} />
          </h1>
        );
      case TextBlockTypes.Heading2:
        return (
          <h2>
            <RichTextElement content={textBlock.text} />
          </h2>
        );
      case TextBlockTypes.Paragraph:
        if (textBlock.text.value === '') {
          return <br />;
        }

        return (
          <p>
            <RichTextElement content={textBlock.text} />
          </p>
        );
      case TextBlockTypes.Quote:
        return (
          <p className="quote">
            <RichTextElement content={textBlock.text} />
          </p>
        );
    }
  } else if ((content as ListBlock).items) {
    return <RichTextListBlock content={content as ListBlock} />;
  }

  throw new Error('Unknown block type!');
};
